import { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { useToast } from 'hooks/useToaster';
import { getCarriers } from 'lib/api/getCarriers';
import {
  NormalizedLoad,
  TMSCarrier,
  createInitCompanyCoreInfo,
} from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { injectSelectedObject } from 'utils/loadInfoAndBuilding';

type CarrierSectionFormProps = {
  formMethods: UseFormReturn<NormalizedLoad>;
  tmsID: Maybe<number>;
  isCreateMode: boolean;
};

export function CarrierSectionForm({
  formMethods,
  tmsID,
  isCreateMode,
}: CarrierSectionFormProps): JSX.Element {
  const { toast } = useToast();
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(false);
  const [fetchedCarriers, setFetchedCarriers] =
    useState<Maybe<TMSCarrier[]>>(null);

  const watchedCustomer = formMethods.watch('customer');
  const watchedCarrier = formMethods.watch('carrier');
  const watchedCarrierExternalTMSID = watchedCarrier?.externalTMSID;

  const selectedCustomerExternalTMSID =
    ((watchedCustomer as any)?.customerExternalTMSID as string) ||
    ((watchedCustomer as any)?.customerexternalTMSID as string) ||
    watchedCustomer?.externalTMSID ||
    '';

  const previousCustomerExternalTMSIDRef = useRef('');
  const carrierFetchRequestIdRef = useRef(0);

  const clearCarrierSelection = (shouldDirty: boolean) => {
    formMethods.setValue('carrier.externalTMSID', '', { shouldDirty });
    formMethods.setValue('carrier.name', '', { shouldDirty });
    formMethods.setValue('carrier.dotNumber', '', { shouldDirty });
    formMethods.setValue('carrier.phone', '', { shouldDirty });
    formMethods.setValue('carrier.email', '', { shouldDirty });
  };

  useEffect(() => {
    const previousCustomerExternalTMSID =
      previousCustomerExternalTMSIDRef.current;
    const hasCustomerChanged =
      !!previousCustomerExternalTMSID &&
      previousCustomerExternalTMSID !== selectedCustomerExternalTMSID;

    if (hasCustomerChanged) {
      setFetchedCarriers(null);
      clearCarrierSelection(true);
    }

    previousCustomerExternalTMSIDRef.current = selectedCustomerExternalTMSID;

    if (!tmsID || !selectedCustomerExternalTMSID) {
      setFetchedCarriers(null);
      clearCarrierSelection(true);
      return;
    }

    const fetchCarriers = async () => {
      const requestId = ++carrierFetchRequestIdRef.current;
      setIsLoadingCarriers(true);
      const res = await getCarriers(
        tmsID,
        false,
        selectedCustomerExternalTMSID
      );

      // Guard against stale responses when customer switches quickly.
      if (requestId !== carrierFetchRequestIdRef.current) {
        return;
      }

      if (res.isOk()) {
        const { carrierList, preferredCarrierExternalTMSID } = res.value;
        setFetchedCarriers(carrierList);

        const preferred = preferredCarrierExternalTMSID?.trim();
        const existingCarrierId = formMethods
          .getValues('carrier.externalTMSID')
          ?.trim();

        if (isCreateMode && preferred && !existingCarrierId) {
          const preferredRow = carrierList.find(
            (c) => String(c.externalTMSID).trim() === preferred
          );
          if (preferredRow) {
            formMethods.setValue(
              'carrier.externalTMSID',
              preferredRow.externalTMSID,
              { shouldDirty: false }
            );
            formMethods.setValue('carrier.name', preferredRow.name ?? '', {
              shouldDirty: false,
            });
            formMethods.setValue(
              'carrier.dotNumber',
              preferredRow.dotNumber ?? '',
              {
                shouldDirty: false,
              }
            );
            formMethods.setValue('carrier.phone', preferredRow.phone ?? '', {
              shouldDirty: false,
            });
            formMethods.setValue('carrier.email', preferredRow.email ?? '', {
              shouldDirty: false,
            });
          }
        }
      } else {
        setFetchedCarriers(null);
        const isWarning = res.error.level === 'warning';
        toast({
          description:
            res.error.message || 'Error while fetching carrier list.',
          variant: isWarning ? 'warning' : 'destructive',
        });
      }

      if (requestId === carrierFetchRequestIdRef.current) {
        setIsLoadingCarriers(false);
      }
    };

    fetchCarriers();
  }, [tmsID, selectedCustomerExternalTMSID, formMethods, toast, isCreateMode]);

  const carriersForSelect = useMemo(() => {
    const list = fetchedCarriers ?? [];
    if (!watchedCarrier?.externalTMSID?.trim()) {
      return list;
    }

    const fromForm: TMSCarrier = {
      ...createInitCompanyCoreInfo(),
      ID: undefined,
      externalTMSID: watchedCarrier.externalTMSID,
      name: watchedCarrier.name ?? '',
      dotNumber: watchedCarrier.dotNumber ?? '',
      phone: watchedCarrier.phone ?? '',
      email: watchedCarrier.email ?? '',
    };

    return injectSelectedObject(fromForm, list);
  }, [fetchedCarriers, watchedCarrier]);

  useEffect(() => {
    if (!watchedCarrierExternalTMSID) {
      return;
    }

    const selectedCarrier = carriersForSelect.find(
      (carrier) => carrier.externalTMSID === watchedCarrierExternalTMSID
    );

    if (!selectedCarrier) {
      return;
    }

    formMethods.setValue(
      'carrier.externalTMSID',
      selectedCarrier.externalTMSID,
      { shouldDirty: false }
    );
    formMethods.setValue('carrier.name', selectedCarrier.name, {
      shouldDirty: false,
    });
    formMethods.setValue('carrier.dotNumber', selectedCarrier.dotNumber, {
      shouldDirty: false,
    });
    formMethods.setValue('carrier.phone', selectedCarrier.phone, {
      shouldDirty: false,
    });
    formMethods.setValue('carrier.email', selectedCarrier.email, {
      shouldDirty: false,
    });
  }, [watchedCarrierExternalTMSID, carriersForSelect, formMethods]);

  const carrierOptions = useMemo(
    () =>
      carriersForSelect.map((carrier) => ({
        value: carrier.externalTMSID,
        label: carrier.name,
      })),
    [carriersForSelect]
  );

  return (
    <Flex direction='col'>
      <Label name='carrier.externalTMSID'>Carrier</Label>
      <Controller
        name='carrier.externalTMSID'
        control={formMethods.control}
        render={({ field }) => (
          <Select
            showSearch
            className='h-8'
            placeholder={isLoadingCarriers ? 'Loading carriers...' : 'Choose'}
            optionFilterProp='children'
            onChange={field.onChange}
            value={field.value}
            options={carrierOptions}
            disabled={isLoadingCarriers || !selectedCustomerExternalTMSID}
          />
        )}
      />
      {!selectedCustomerExternalTMSID && (
        <Typography variant='body-xs' className='text-neutral-500 mt-1'>
          Select customer first to load carriers.
        </Typography>
      )}
    </Flex>
  );
}
